import { useEffect, useState } from "react";

const storageKeyFor = (orgId: string) => `pixi-wizard-step:${orgId}`;

const clampStep = (value: number, totalSteps: number): number =>
  Math.min(Math.max(value, 1), totalSteps);

const readSavedStep = (
  orgId: string | undefined,
  totalSteps: number,
): number => {
  if (!orgId) return 1;
  try {
    const raw = window.localStorage.getItem(storageKeyFor(orgId));
    if (!raw) return 1;
    const parsed = Number.parseInt(raw, 10);
    if (Number.isNaN(parsed) || parsed < 1 || parsed > totalSteps) return 1;
    return parsed;
  } catch {
    return 1;
  }
};

interface UsePixiWizardStep {
  step: number;
  goNext: () => void;
  goBack: () => void;
  /**
   * Jump to a step (from Checkout's pencils). Navigation then resumes
   * sequentially from there — Continue advances to the next step in order, it
   * does not return to Checkout.
   */
  editStep: (target: number) => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

export const usePixiWizardStep = (
  orgId: string | undefined,
  totalSteps: number,
): UsePixiWizardStep => {
  const [step, setStep] = useState(() => readSavedStep(orgId, totalSteps));

  useEffect(() => {
    if (!orgId) return;
    try {
      window.localStorage.setItem(storageKeyFor(orgId), String(step));
    } catch {
      // localStorage may be unavailable (private mode, quota); ignore.
    }
  }, [orgId, step]);

  return {
    step,
    goNext: () => setStep((s) => clampStep(s + 1, totalSteps)),
    goBack: () => setStep((s) => clampStep(s - 1, totalSteps)),
    editStep: (target: number) => setStep(clampStep(target, totalSteps)),
    isFirstStep: step === 1,
    isLastStep: step === totalSteps,
  };
};
